import { c as _c } from "react/compiler-runtime";
import * as d3 from 'd3';
import { useCallback, useEffect, useRef, useState } from 'react';
import useMobileLayout from '@/hooks/use-mobile-layout';
type DataPoint = {
  label: string;
  values: Record<string, number>;
};
type Series = {
  key: string;
  label: string;
  color?: string;
  type?: 'bar' | 'line';
};
type BarChartProps = {
  data: DataPoint[];
  series: Series[];
  currency?: string;
};
const PALETTE = ['#6366f1', '#94a3b8', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4'];

// Intl.NumberFormat throws on invalid ISO currency codes; AI-emitted currency
// strings (e.g. "btc") would crash the entire chart. Fall back to USD.
const safeCurrencyFormat = (options: Intl.NumberFormatOptions, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', {
      ...options,
      currency
    });
  } catch {
    return new Intl.NumberFormat('en-US', {
      ...options,
      currency: 'USD'
    });
  }
};
const toFinite = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmtAxis = (v: number, currency = 'USD') => safeCurrencyFormat({
  style: 'currency',
  notation: 'compact',
  maximumFractionDigits: 1
}, currency).format(v);
const fmtValue = (v: number, currency = 'USD') => safeCurrencyFormat({
  style: 'currency',
  maximumFractionDigits: 0
}, currency).format(v);
const BarChart = t0 => {
  const $ = _c(39);
  const {
    data: t1,
    series: t2,
    currency: t3
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const data = t4;
  const series = t2 === undefined ? [] : t2;
  const currency = t3 === undefined ? "USD" : t3;
  const isMobile = useMobileLayout();
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [tooltip, setTooltip] = useState(null);
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = node => {
      if (node) {
        containerRef.current = node;
        setWidth(node.clientWidth);
        const ro = new ResizeObserver(entries => {
          const w = entries[0]?.contentRect.width;
          if (w && w > 0) {
            setWidth(w);
          }
        });
        ro.observe(node);
        return () => ro.disconnect();
      }
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const measuredRef = t5;
  const colorMap = Object.fromEntries(series.map(_temp));
  let t6;
  if ($[3] !== series) {
    t6 = series.filter(_temp2);
    $[3] = series;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const barSeries = t6;
  let t7;
  if ($[5] !== series) {
    t7 = series.filter(_temp3);
    $[5] = series;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const lineSeries = t7;
  let t8;
  if ($[7] !== barSeries || $[8] !== colorMap || $[9] !== currency || $[10] !== data || $[11] !== isMobile || $[12] !== lineSeries || $[13] !== series || $[14] !== setTooltip || $[15] !== width) {
    t8 = () => {
      if (!svgRef.current || !data.length || !width || !series.length) {
        return;
      }
      const el = svgRef.current;
      const svg = d3.select(el);
      svg.selectAll("*").remove();
      const W = width;
      const margin = {
        top: 16,
        right: 20,
        bottom: 40,
        left: 56
      };
      const iw = W - margin.left - margin.right;
      const ih = 320 - margin.top - margin.bottom;
      svg.attr("width", W).attr("height", 320);
      const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
      const x0 = d3.scaleBand().domain(data.map(_temp4)).range([0, iw]).paddingInner(0.3).paddingOuter(0.15);
      const x1 = d3.scaleBand().domain(barSeries.map(_temp5)).range([0, x0.bandwidth()]).padding(0.08);
      const allValues = data.flatMap(d_0 => series.map(s_3 => toFinite(d_0.values[s_3.key])));
      const yMax = d3.max(allValues) ?? 0;
      const y = d3.scaleLinear().domain([0, yMax * 1.12]).range([ih, 0]).nice();
      const yTicks = y.ticks(5);
      g.append("g").selectAll("line").data(yTicks).join("line").attr("x1", 0).attr("y1", d_1 => y(d_1)).attr("x2", iw).attr("y2", d_2 => y(d_2)).attr("stroke", "var(--border-color-tertiary)").attr("stroke-width", 1);
      g.append("g").selectAll("text").data(yTicks).join("text").attr("x", -12).attr("y", d_3 => y(d_3)).attr("text-anchor", "end").attr("dominant-baseline", "middle").attr("font-size", 11).attr("font-family", "inherit").attr("fill", "var(--text-color-tertiary)").text(d_4 => fmtAxis(d_4, currency));
      const labelStep = isMobile ? Math.max(1, Math.ceil((Math.max(0, ...data.map(_temp6)) * 6.5 + 8) / x0.step())) : 1;
      g.append("g").selectAll("text").data(data.filter((_, index) => index % labelStep === 0)).join("text").attr("x", d_6 => (x0(d_6.label) ?? 0) + x0.bandwidth() / 2).attr("y", ih + 24).attr("text-anchor", "middle").attr("font-size", 11).attr("font-family", "inherit").attr("fill", "var(--text-color-tertiary)").text(_temp7);
      for (const s_4 of barSeries) {
        g.selectAll(`rect.bar-${s_4.key}`).data(data).join("rect").attr("class", `bar-${s_4.key}`).attr("x", d_8 => (x0(d_8.label) ?? 0) + (x1(s_4.key) ?? 0)).attr("y", d_9 => y(toFinite(d_9.values[s_4.key]))).attr("width", x1.bandwidth()).attr("height", d_10 => ih - y(toFinite(d_10.values[s_4.key]))).attr("fill", colorMap[s_4.key]).attr("rx", 2).attr("opacity", 0.9);
      }
      g.selectAll("rect.hit").data(data).join("rect").attr("class", "hit").attr("x", d_11 => x0(d_11.label) ?? 0).attr("y", 0).attr("width", x0.bandwidth()).attr("height", ih).attr("fill", "transparent").attr("cursor", "pointer").on("mouseenter", (event, d_12) => {
        const rect = el.getBoundingClientRect();
        setTooltip({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
          label: d_12.label,
          values: series.map(s_5 => ({
            key: s_5.key,
            seriesLabel: s_5.label,
            value: toFinite(d_12.values[s_5.key]),
            color: colorMap[s_5.key]
          }))
        });
      }).on("mousemove", event_0 => {
        const rect_0 = el.getBoundingClientRect();
        setTooltip(prev => prev ? {
          ...prev,
          x: event_0.clientX - rect_0.left,
          y: event_0.clientY - rect_0.top
        } : null);
      }).on("mouseleave", () => setTooltip(null));
      for (const s_6 of lineSeries) {
        const line = d3.line().x(d_13 => (x0(d_13.label) ?? 0) + x0.bandwidth() / 2).y(d_14 => y(toFinite(d_14.values[s_6.key]))).curve(d3.curveMonotoneX);
        g.append("path").datum(data).attr("fill", "none").attr("stroke", colorMap[s_6.key]).attr("stroke-width", 2).attr("d", line);
        g.selectAll(`circle.dot-${s_6.key}`).data(data).join("circle").attr("class", `dot-${s_6.key}`).attr("cx", d_15 => (x0(d_15.label) ?? 0) + x0.bandwidth() / 2).attr("cy", d_16 => y(toFinite(d_16.values[s_6.key]))).attr("r", 3.5).attr("fill", colorMap[s_6.key]).attr("stroke", "var(--background-color-primary)").attr("stroke-width", 2);
      }
      return () => {
        svg.selectAll("*").remove();
      };
    };
    $[7] = barSeries;
    $[8] = colorMap;
    $[9] = currency;
    $[10] = data;
    $[11] = isMobile;
    $[12] = lineSeries;
    $[13] = series;
    $[14] = setTooltip;
    $[15] = width;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== barSeries || $[18] !== colorMap || $[19] !== data || $[20] !== isMobile || $[21] !== lineSeries || $[22] !== series || $[23] !== width) {
    t9 = [data, series, barSeries, lineSeries, colorMap, width, isMobile];
    $[17] = barSeries;
    $[18] = colorMap;
    $[19] = data;
    $[20] = isMobile;
    $[21] = lineSeries;
    $[22] = series;
    $[23] = width;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  useEffect(t8, t9);
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <svg ref={svgRef} role="img" aria-label="Bar chart" style={{
      display: "block",
      width: "100%"
    }} />;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== currency || $[27] !== tooltip || $[28] !== width) {
    t11 = tooltip && <div className="pointer-events-none absolute z-10 rounded-md px-3 py-2 shadow-md min-w-[140px]" style={{
      left: Math.min(tooltip.x + 12, width - 170),
      top: tooltip.y - 16,
      backgroundColor: "var(--text-color-primary)"
    }}><p className="text-[11px] font-semibold mb-1" style={{
        color: "var(--background-color-primary)"
      }}>{tooltip.label}</p>{tooltip.values.map(v => <div key={v.key} className="flex items-center gap-1.5"><div className="h-2 w-2 flex-none rounded-full" style={{
          backgroundColor: v.color
        }} /><p className="text-[10px]" style={{
          color: "#a0a0a0"
        }}>{v.seriesLabel}: {fmtValue(v.value, currency)}</p></div>)}</div>;
    $[26] = currency;
    $[27] = tooltip;
    $[28] = width;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== measuredRef || $[31] !== t11) {
    t12 = <div ref={measuredRef} className="relative">{t10}{t11}</div>;
    $[30] = measuredRef;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== colorMap || $[34] !== series) {
    t13 = series.length > 1 && <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2">{series.map(s_7 => <div key={s_7.key} className="flex items-center gap-2">{s_7.type === "line" ? <div className="h-0.5 w-4 flex-none rounded-full" style={{
          backgroundColor: colorMap[s_7.key]
        }} /> : <div className="h-2.5 w-2.5 flex-none rounded-sm" style={{
          backgroundColor: colorMap[s_7.key]
        }} />}<span className="text-caption-1 text-tertiary fp-text">{s_7.label}</span></div>)}</div>;
    $[33] = colorMap;
    $[34] = series;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t12 || $[37] !== t13) {
    t14 = <div className="w-full">{t12}{t13}</div>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
};
export default BarChart;
function _temp(s, i) {
  return [s.key, s.color ?? PALETTE[i % PALETTE.length]];
}
function _temp2(s_0) {
  return s_0.type !== "line";
}
function _temp3(s_1) {
  return s_1.type === "line";
}
function _temp4(d) {
  return d.label;
}
function _temp5(s_2) {
  return s_2.key;
}
function _temp6(d_5) {
  return String(d_5.label ?? "").length;
}
function _temp7(d_7) {
  return d_7.label;
}